import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { smsClient } from '../client';
import type { Aggregator, AggregatorCreate, AggregatorUpdate } from '../types';

function normalizeOptionalNulls<T extends { aggregator_type?: string | null; account_id?: number | null }>(
  body: T,
): T {
  const next = { ...body };
  const type = typeof next.aggregator_type === 'string' ? next.aggregator_type.trim() : next.aggregator_type;
  if (type == null || type === '') {
    next.aggregator_type = null;
  } else {
    next.aggregator_type = type;
  }
  const accountId = next.account_id;
  if (accountId == null || accountId === ('' as unknown) || !Number.isFinite(Number(accountId))) {
    next.account_id = null;
  } else {
    next.account_id = Number(accountId);
  }
  return next;
}

export const aggregatorsAdapter: CrudAdapter<Aggregator, AggregatorCreate, AggregatorUpdate> = {
  async list({ offset, limit, sortName, sortDirection }) {
    const { data, error } = await smsClient.GET('/aggregators/', {
      params: {
        query: {
          offset,
          limit,
          'sort.name': sortName ?? 'id',
          'sort.direction': (sortDirection ?? 'asc') as 'asc' | 'desc',
        },
      },
    });
    if (error) throw error;
    return { items: data?.items ?? [], total: data?.total ?? 0 };
  },
  async create(payload) {
    const body: AggregatorCreate = normalizeOptionalNulls({ ...payload });
    const smscId = typeof body.smsc_id === 'string' ? body.smsc_id.trim() : body.smsc_id;
    if (smscId == null || smscId === '') {
      delete body.smsc_id;
    } else {
      body.smsc_id = smscId;
    }
    // OpenAPI still marks smsc_id required; backend accepts omit and generates it.
    const { data, error } = await smsClient.POST('/aggregators/', {
      body: body as AggregatorCreate & { smsc_id: string },
    });
    if (error) throw error;
    return { id: data?.id ?? 0 };
  },
  async update(payload) {
    const body = normalizeOptionalNulls({ ...payload });
    const { data, error } = await smsClient.PUT('/aggregators/', { body });
    if (error) throw error;
    return (data ?? body) as Aggregator;
  },
  async byKey(id) {
    const { data, error } = await smsClient.GET('/aggregators/{item_id}', {
      params: { path: { item_id: Number(id) } },
    });
    if (error) throw error;
    return data as Aggregator;
  },
  async remove(id) {
    const { error } = await smsClient.DELETE('/aggregators/{item_id}', {
      params: { path: { item_id: Number(id) } },
    });
    if (error) throw error;
  },
};

export const aggregatorsStore = useCrudDataSource(aggregatorsAdapter);
